import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { adminApi } from '@/api/endpoints';
import { ErrorState, Loading } from '@/components/common/State';
import { formatNumber, formatWon } from '@/utils/format';
import type { StatPoint } from '@/api/types';

const PERIODS = [
  { value: 'DAY', label: '일별' },
  { value: 'WEEK', label: '주별' },
  { value: 'MONTH', label: '월별' },
  { value: 'YEAR', label: '연별' },
] as const;

export default function AdminDashboardPage() {
  const [period, setPeriod] = useState<string>('DAY');

  const summary = useQuery({ queryKey: ['admin', 'summary'], queryFn: adminApi.summary });
  const sales = useQuery({
    queryKey: ['admin', 'sales', period],
    queryFn: () => adminApi.sales({ period }),
  });
  const signups = useQuery({
    queryKey: ['admin', 'signups', period],
    queryFn: () => adminApi.signups({ period }),
  });
  const top = useQuery({ queryKey: ['admin', 'topProducts'], queryFn: () => adminApi.topProducts({ limit: 5 }) });

  if (summary.isLoading) return <Loading />;
  if (summary.isError) return <ErrorState error={summary.error} onRetry={summary.refetch} />;

  const s = summary.data!;

  return (
    <>
      <h1 className="panel-title">대시보드</h1>

      <div className="stat-grid">
        <StatCard label="오늘 매출" value={formatWon(s.salesToday)} sub={`주문 ${s.ordersToday}건`} />
        <StatCard label="이번 달 매출" value={formatWon(s.salesThisMonth)} sub={`주문 ${s.ordersThisMonth}건`} />
        <StatCard label="전체 회원" value={formatNumber(s.totalUsers)} sub={`오늘 +${s.newUsersToday}`} />
        <StatCard label="판매중 상품" value={formatNumber(s.onSaleProducts)} sub={`재고부족 ${s.lowStockOptions}`} />
        <StatCard label="처리 대기 주문" value={formatNumber(s.pendingOrders)} sub="결제완료 + 준비중" />
        <StatCard label="취소/환불 대기" value={formatNumber(s.pendingCancels)} sub="승인 필요" />
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        {PERIODS.map((p) => (
          <button
            key={p.value}
            type="button"
            className={`btn btn--sm ${period === p.value ? 'btn--primary' : 'btn--outline'}`}
            onClick={() => setPeriod(p.value)}
          >
            {p.label}
          </button>
        ))}
      </div>

      <div style={{ display: 'grid', gap: 20, gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))' }}>
        <ChartCard
          title="매출 추이"
          subtitle={`합계 ${formatWon(sales.data?.totalAmount ?? 0)}`}
          points={sales.data?.points ?? []}
          valueOf={(p) => p.amount}
          loading={sales.isLoading}
        />
        <ChartCard
          title="신규 가입 추이"
          subtitle={`합계 ${formatNumber(signups.data?.totalCount ?? 0)}명`}
          points={signups.data?.points ?? []}
          valueOf={(p) => p.count}
          loading={signups.isLoading}
        />
      </div>

      <div className="card" style={{ marginTop: 20 }}>
        <div className="card__body">
          <h2 style={{ fontSize: 16, marginTop: 0 }}>판매 상위 상품</h2>
          {top.isLoading && <Loading />}
          {top.data?.length === 0 && <p className="muted">판매 데이터가 없습니다.</p>}
          <div className="table-wrap">
            {top.data && top.data.length > 0 && (
              <table className="table">
                <thead>
                  <tr>
                    <th>상품</th>
                    <th>판매수량</th>
                    <th>매출</th>
                  </tr>
                </thead>
                <tbody>
                  {top.data.map((product) => (
                    <tr key={product.productId}>
                      <td className="wrap">{product.name}</td>
                      <td>{formatNumber(product.quantity)}</td>
                      <td>{formatWon(product.amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

function StatCard({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div className="stat-card">
      <div className="stat-card__label">{label}</div>
      <div className="stat-card__value">{value}</div>
      {sub && <div className="muted" style={{ fontSize: 12 }}>{sub}</div>}
    </div>
  );
}

/** 외부 차트 라이브러리 없이 CSS 만으로 그리는 막대 그래프. */
function ChartCard({
  title,
  subtitle,
  points,
  valueOf,
  loading,
}: {
  title: string;
  subtitle: string;
  points: StatPoint[];
  valueOf: (p: StatPoint) => number;
  loading: boolean;
}) {
  const max = Math.max(1, ...points.map(valueOf));

  return (
    <div className="card">
      <div className="card__body">
        <h2 style={{ fontSize: 16, marginTop: 0 }}>{title}</h2>
        <p className="muted" style={{ marginTop: 0 }}>
          {subtitle}
        </p>
        {loading ? (
          <Loading />
        ) : points.length === 0 ? (
          <p className="muted">데이터가 없습니다.</p>
        ) : (
          <div className="bar-chart">
            {points.map((point) => (
              <div className="bar-chart__col" key={point.bucket} title={`${point.bucket}: ${valueOf(point)}`}>
                <div
                  className="bar-chart__bar"
                  style={{ height: `${(valueOf(point) / max) * 100}%` }}
                />
                <div className="bar-chart__label">{point.bucket.slice(-5)}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
